import {
  DEFAULT_NAVMESH_CONFIG,
  extractBoxes,
  extractLadders,
  type LadderRegion,
  type MapJson,
  type NavMeshConfig,
  type NavPoint,
  type SolidBox,
} from "./navmesh";

export type GeneratedNavDir = "north" | "east" | "south" | "west";
export type GeneratedNavCorner = "nw" | "ne" | "se" | "sw";

export interface GeneratedNavNodeLink {
  to: number;
  obstacleHeight: number;
  obstacleStartDist: number;
  obstacleEndDist: number;
}

export interface GeneratedNavNode {
  id: number;
  x: number;
  y: number;
  z: number;
  normal: NavPoint;
  links: Partial<Record<GeneratedNavDir, GeneratedNavNodeLink>>;
  crouch: Record<GeneratedNavCorner, boolean>;
  blocked: Record<GeneratedNavCorner, boolean>;
  covered: boolean;
  areaId: number | null;
}

export interface GeneratedNavArea {
  id: number;
  nw: NavPoint;
  ne: NavPoint;
  se: NavPoint;
  sw: NavPoint;
  attrs: number;
  connections: Partial<Record<GeneratedNavDir, number[]>>;
  nodes: Record<GeneratedNavCorner, number>;
}

export type GeneratedNavLinkKind = "walk" | "jump" | "drop" | "crouch" | "ladder";

export interface GeneratedNavAreaLink {
  from: number;
  to: number;
  distance: number;
  heightDelta: number;
  obstacleHeight: number;
  cost: number;
  kind: GeneratedNavLinkKind;
}

/**
 * Per-traversal path cost functor, mirroring lever-engine's `IPathCost`
 * (`game/server/NextBot/NextBotPath.h`):
 *
 *   virtual float operator()( CNavArea *area, CNavArea *fromArea,
 *                             const CNavLadder *ladder, ...
 *                             float length ) const = 0;
 *
 * Returning a negative number forbids the traversal (lever's `-1` dead-end);
 * otherwise the function returns the *cumulative* cost-so-far. Because the
 * functor is supplied per search, the cost can depend on the specific bot's
 * abilities (max jump height, may-not-cross-death-drops), a danger/avoid
 * influence layer, or retreat-vs-advance policy, exactly like
 * `CSimpleBotPathCost`. The default functor (`makeStaticPathCost`) reproduces
 * the previous static per-link cost so existing callers are unchanged.
 */
export type GeneratedPathCost = (
  area: GeneratedNavArea,
  fromArea: GeneratedNavArea | null,
  link: GeneratedNavAreaLink | null,
  lengthSoFar: number,
) => number;

/**
 * Tunable per-bot inputs for the default cost functor. Mirrors the
 * `ILocomotion` getters lever's `CSimpleBotPathCost` consults
 * (`GetStepHeight`/`GetMaxJumpHeight`/`GetDeathDropHeight`) plus optional
 * danger weighting and ladder ability.
 */
export interface PathCostOptions {
  stepHeight?: number;
  maxJumpHeight?: number;
  deathDropHeight?: number;
  /** When false, ladder links are forbidden (returned as a dead-end). */
  canUseLadders?: boolean;
  /** Multiplier applied to the jump penalty (lever uses 5*dist). */
  jumpPenaltyScale?: number;
  /** Optional danger/avoid weight added per area entered (influence map). */
  dangerCost?: (area: GeneratedNavArea) => number;
}

export interface GeneratedNavMesh {
  nodes: GeneratedNavNode[];
  areas: GeneratedNavArea[];
  areaLinks: Record<string, GeneratedNavAreaLink>;
  source: "generated";
  generationStepSize: number;
  bounds: { minX: number; maxX: number; minZ: number; maxZ: number };
  stats: {
    seeds: number;
    nodeLinks: number;
    oneWayLinks: number;
    blockedQuadrants: number;
    crouchQuadrants: number;
    obstacleLinks: number;
    isolatedNodes: number;
    uncoveredNodes: number;
  };
}

export interface GeneratedNavPath {
  startAreaId: number;
  goalAreaId: number;
  areaIds: number[];
  points: NavPoint[];
  /**
   * Traversal kind for each point, aligned with `points`: `segmentKinds[i]` is
   * the link kind used to move from `points[i-1]` to `points[i]`. Index 0 is
   * always `"walk"`. Mirrors lever's per-segment `Path::Segment::type`
   * (`ON_GROUND`/`CLIMB_UP`/`DROP_DOWN`/`JUMP_OVER_GAP`/`LADDER_*`) so the path
   * follower can drive the matching locomotion verb.
   */
  segmentKinds: GeneratedNavLinkKind[];
  reachedGoal: boolean;
  endpointDistance: number;
}

export interface GeneratedBlockedEdgeSet {
  has(key: string): boolean;
}

export interface GeneratedNavStepDebug {
  from: NavPoint;
  target: NavPoint;
  direction: GeneratedNavDir;
  ok: boolean;
  end: NavPoint;
  normal: NavPoint;
  obstacleHeight: number;
  obstacleStartDist: number;
  obstacleEndDist: number;
  reason: string;
  fromGround: NavPoint | null;
  targetGround: NavPoint | null;
}

interface GeneratedNavConfig extends NavMeshConfig {
  maxAreaSize: number;
  maxSampleNodes: number;
  deathDrop: number;
  slopeLimit: number;
  jumpPenalty: number;
  dropPenalty: number;
  crouchPenalty: number;
}

interface TraceResult {
  ok: boolean;
  end: NavPoint;
  normal: NavPoint;
  obstacleHeight: number;
  obstacleStartDist: number;
  obstacleEndDist: number;
  reason?: string;
}

const DIRS: GeneratedNavDir[] = ["north", "east", "south", "west"];
const OPPOSITE: Record<GeneratedNavDir, GeneratedNavDir> = {
  north: "south",
  east: "west",
  south: "north",
  west: "east",
};
const DIR_DELTA: Record<GeneratedNavDir, { x: number; z: number }> = {
  north: { x: 0, z: -1 },
  east: { x: 1, z: 0 },
  south: { x: 0, z: 1 },
  west: { x: -1, z: 0 },
};
const CORNERS: GeneratedNavCorner[] = ["nw", "ne", "se", "sw"];
const EMPTY_CORNERS = (): Record<GeneratedNavCorner, boolean> => ({ nw: false, ne: false, se: false, sw: false });

class MinHeap {
  private items: Array<{ id: number; f: number }> = [];

  get size(): number {
    return this.items.length;
  }

  push(item: { id: number; f: number }): void {
    this.items.push(item);
    let index = this.items.length - 1;
    while (index > 0) {
      const parent = (index - 1) >> 1;
      if (this.items[parent].f <= item.f) break;
      this.items[index] = this.items[parent];
      index = parent;
    }
    this.items[index] = item;
  }

  pop(): { id: number; f: number } | null {
    if (!this.items.length) return null;
    const top = this.items[0];
    const last = this.items.pop()!;
    if (this.items.length) {
      let index = 0;
      while (true) {
        const left = index * 2 + 1;
        const right = left + 1;
        if (left >= this.items.length) break;
        const child = right < this.items.length && this.items[right].f < this.items[left].f ? right : left;
        if (this.items[child].f >= last.f) break;
        this.items[index] = this.items[child];
        index = child;
      }
      this.items[index] = last;
    }
    return top;
  }
}

export function buildGeneratedNavMesh(map: MapJson, options: Partial<GeneratedNavConfig> = {}): GeneratedNavMesh {
  const cfg: GeneratedNavConfig = {
    ...DEFAULT_NAVMESH_CONFIG,
    maxAreaSize: 50,
    maxSampleNodes: 50000,
    deathDrop: 240,
    slopeLimit: 0.7,
    jumpPenalty: 180,
    dropPenalty: 120,
    crouchPenalty: 80,
    ...options,
  };
  const boxes = extractBoxes(map);
  const bounds = navBounds(boxes, map, cfg);
  const nodes: GeneratedNavNode[] = [];
  const nodeByKey = new Map<string, number>();
  const seeds = walkableSeeds(map, boxes, cfg);

  const addNode = (point: NavPoint, normal: NavPoint): GeneratedNavNode => {
    const key = nodeKey(point, cfg.cellSize);
    const existing = nodeByKey.get(key);
    if (existing != null) return nodes[existing];
    const node: GeneratedNavNode = {
      id: nodes.length,
      x: point.x,
      y: point.y,
      z: point.z,
      normal,
      links: {},
      crouch: EMPTY_CORNERS(),
      blocked: EMPTY_CORNERS(),
      covered: false,
      areaId: null,
    };
    checkNodeCrouchAndBlocked(node, boxes, cfg);
    nodeByKey.set(key, node.id);
    nodes.push(node);
    return node;
  };

  const stack: GeneratedNavNode[] = [];
  for (const seed of seeds) stack.push(addNode(seed.point, seed.normal));

  while (stack.length && nodes.length < cfg.maxSampleNodes) {
    const node = stack.pop()!;
    for (const dir of DIRS) {
      if (node.links[dir]) continue;
      const delta = DIR_DELTA[dir];
      const target = {
        x: snapToGrid(node.x + delta.x * cfg.cellSize, cfg.cellSize),
        y: node.y,
        z: snapToGrid(node.z + delta.z * cfg.cellSize, cfg.cellSize),
      };
      if (!withinBounds(target, bounds, cfg)) continue;
      const trace = traceAdjacentNode({ x: node.x, y: node.y, z: node.z }, target, boxes, cfg);
      if (!trace.ok) continue;
      const next = addNode(trace.end, trace.normal);
      connectNodes(node, next, dir, trace, cfg);
      const reverse = Math.abs(node.y - next.y) < cfg.jumpHeight + cfg.stepHeight;
      if (reverse && !next.links[OPPOSITE[dir]]) {
        connectNodes(next, node, OPPOSITE[dir], {
          ...trace,
          obstacleStartDist: cfg.cellSize - trace.obstacleEndDist,
          obstacleEndDist: cfg.cellSize - trace.obstacleStartDist,
        }, cfg);
      }
      if (!next.covered && next.id === nodes.length - 1) stack.push(next);
    }
  }

  const areas = createNavAreasFromNodes(nodes, cfg);
  connectGeneratedAreas(nodes, areas);
  const areaLinks = buildAreaLinks(nodes, areas, cfg);
  connectLadderAreas(extractLadders(map, cfg), areas, areaLinks, cfg);
  return {
    nodes,
    areas,
    areaLinks,
    source: "generated",
    generationStepSize: cfg.cellSize,
    bounds,
    stats: summarizeGeneratedNav(nodes, seeds.length),
  };
}

export function findGeneratedNavPath(mesh: GeneratedNavMesh, from: NavPoint, to: NavPoint, blockedEdges?: GeneratedBlockedEdgeSet, costFunc?: GeneratedPathCost): GeneratedNavPath | null {
  const startArea = resolveGeneratedNavArea(mesh, from);
  const goalArea = resolveGeneratedNavArea(mesh, to);
  if (startArea == null || goalArea == null) return null;
  const cost = costFunc ?? makeStaticPathCost();
  const projectedGoal = projectPointToArea(mesh.areas[goalArea], to);
  const endpointDistance = Math.hypot(projectedGoal.x - to.x, projectedGoal.y - to.y, projectedGoal.z - to.z);
  if (startArea === goalArea) {
    return {
      startAreaId: startArea,
      goalAreaId: goalArea,
      areaIds: [startArea],
      points: [projectPointToArea(mesh.areas[startArea], from), projectedGoal],
      segmentKinds: ["walk", "walk"],
      reachedGoal: endpointDistance <= mesh.generationStepSize * 4,
      endpointDistance,
    };
  }

  const areaIds = findGeneratedAreaPath(mesh, startArea, goalArea, cost, blockedEdges) || findClosestGeneratedAreaPath(mesh, startArea, goalArea, cost, blockedEdges);
  if (!areaIds || areaIds.length < 2) return null;
  const traced = generatedAreaPathToPoints(mesh, areaIds, from, to);
  return {
    startAreaId: startArea,
    goalAreaId: goalArea,
    areaIds,
    points: traced.points,
    segmentKinds: traced.kinds,
    reachedGoal: areaIds[areaIds.length - 1] === goalArea && endpointDistance <= mesh.generationStepSize * 4,
    endpointDistance,
  };
}

export function generatedNavAreaEdgeKey(a: number, b: number): string {
  return a < b ? `${a}:${b}` : `${b}:${a}`;
}

export function debugGeneratedNavStep(map: MapJson, from: NavPoint, direction: GeneratedNavDir, options: Partial<GeneratedNavConfig> = {}): GeneratedNavStepDebug {
  const cfg: GeneratedNavConfig = {
    ...DEFAULT_NAVMESH_CONFIG,
    maxAreaSize: 50,
    maxSampleNodes: 50000,
    deathDrop: 240,
    slopeLimit: 0.7,
    jumpPenalty: 180,
    dropPenalty: 120,
    crouchPenalty: 80,
    ...options,
  };
  const boxes = extractBoxes(map);
  const delta = DIR_DELTA[direction];
  const target = {
    x: snapToGrid(from.x + delta.x * cfg.cellSize, cfg.cellSize),
    y: from.y,
    z: snapToGrid(from.z + delta.z * cfg.cellSize, cfg.cellSize),
  };
  const fromGround = findGroundForNode(from, boxes, cfg)?.point || null;
  const trace = traceAdjacentNode(fromGround || from, target, boxes, cfg);
  const targetGround = findGroundForNode(target, boxes, cfg)?.point || null;
  return {
    from,
    target,
    direction,
    ok: trace.ok,
    end: trace.end,
    normal: trace.normal,
    obstacleHeight: trace.obstacleHeight,
    obstacleStartDist: trace.obstacleStartDist,
    obstacleEndDist: trace.obstacleEndDist,
    reason: trace.reason || (trace.ok ? "ok" : "failed"),
    fromGround,
    targetGround,
  };
}

export function resolveGeneratedNavArea(mesh: GeneratedNavMesh, point: NavPoint): number | null {
  let best: { id: number; score: number } | null = null;
  const maxHorizontal = mesh.generationStepSize * 12;
  const maxVertical = DEFAULT_NAVMESH_CONFIG.jumpHeight + DEFAULT_NAVMESH_CONFIG.stepHeight;
  for (const area of mesh.areas) {
    const bounds = areaBounds(area);
    const inside = point.x >= bounds.minX && point.x <= bounds.maxX && point.z >= bounds.minZ && point.z <= bounds.maxZ;
    const closest = closestPointOnArea(area, point);
    const vertical = Math.abs(closest.y - point.y);
    const horizontal = Math.hypot(closest.x - point.x, closest.z - point.z);
    if (inside && vertical <= maxVertical) return area.id;
    const score = horizontal + vertical * 2;
    if (horizontal <= maxHorizontal && vertical <= maxVertical && (!best || score < best.score)) {
      best = { id: area.id, score };
    }
  }
  return best?.id ?? null;
}

function findGeneratedAreaPath(mesh: GeneratedNavMesh, startId: number, goalId: number, costFunc: GeneratedPathCost, blockedEdges?: GeneratedBlockedEdgeSet): number[] | null {
  const cameFrom = new Int32Array(mesh.areas.length);
  const gScore = new Float64Array(mesh.areas.length);
  const closed = new Uint8Array(mesh.areas.length);
  cameFrom.fill(-1);
  gScore.fill(Infinity);
  const goal = areaCenter(mesh.areas[goalId]);
  const heuristic = (id: number) => dist2D(areaCenter(mesh.areas[id]), goal);
  const open = new MinHeap();
  gScore[startId] = costFunc(mesh.areas[startId], null, null, 0);
  if (gScore[startId] < 0) return null;
  open.push({ id: startId, f: heuristic(startId) });

  while (open.size) {
    const current = open.pop()!;
    if (closed[current.id]) continue;
    if (current.id === goalId) return reconstructGeneratedAreaPath(cameFrom, goalId);
    closed[current.id] = 1;
    for (const nextId of generatedAreaNeighbors(mesh.areas[current.id])) {
      if (blockedEdges?.has(generatedNavAreaEdgeKey(current.id, nextId))) continue;
      if (closed[nextId]) continue;
      const link = mesh.areaLinks[`${current.id}:${nextId}`] ?? null;
      const tentative = costFunc(mesh.areas[nextId], mesh.areas[current.id], link, gScore[current.id]);
      if (tentative < 0 || !Number.isFinite(tentative)) continue;
      if (tentative < gScore[nextId]) {
        cameFrom[nextId] = current.id;
        gScore[nextId] = tentative;
        open.push({ id: nextId, f: tentative + heuristic(nextId) });
      }
    }
  }
  return null;
}

function findClosestGeneratedAreaPath(mesh: GeneratedNavMesh, startId: number, goalId: number, costFunc: GeneratedPathCost, blockedEdges?: GeneratedBlockedEdgeSet): number[] | null {
  const cameFrom = new Int32Array(mesh.areas.length);
  const gScore = new Float64Array(mesh.areas.length);
  const closed = new Uint8Array(mesh.areas.length);
  cameFrom.fill(-1);
  gScore.fill(Infinity);
  const goal = areaCenter(mesh.areas[goalId]);
  const heuristic = (id: number) => dist2D(areaCenter(mesh.areas[id]), goal);
  let bestId = startId;
  let bestScore = heuristic(startId);
  const open = new MinHeap();
  gScore[startId] = costFunc(mesh.areas[startId], null, null, 0);
  if (gScore[startId] < 0) return null;
  open.push({ id: startId, f: bestScore });

  while (open.size) {
    const current = open.pop()!;
    if (closed[current.id]) continue;
    if (current.id === goalId) return reconstructGeneratedAreaPath(cameFrom, goalId);
    closed[current.id] = 1;
    const score = heuristic(current.id);
    if (score < bestScore) {
      bestScore = score;
      bestId = current.id;
    }
    for (const nextId of generatedAreaNeighbors(mesh.areas[current.id])) {
      if (blockedEdges?.has(generatedNavAreaEdgeKey(current.id, nextId))) continue;
      if (closed[nextId]) continue;
      const link = mesh.areaLinks[`${current.id}:${nextId}`] ?? null;
      const tentative = costFunc(mesh.areas[nextId], mesh.areas[current.id], link, gScore[current.id]);
      if (tentative < 0 || !Number.isFinite(tentative)) continue;
      if (tentative < gScore[nextId]) {
        cameFrom[nextId] = current.id;
        gScore[nextId] = tentative;
        open.push({ id: nextId, f: tentative + heuristic(nextId) });
      }
    }
  }

  return bestId === startId ? null : reconstructGeneratedAreaPath(cameFrom, bestId);
}

function generatedAreaPathToPoints(mesh: GeneratedNavMesh, areaIds: number[], from: NavPoint, to: NavPoint): { points: NavPoint[]; kinds: GeneratedNavLinkKind[] } {
  const points = [projectPointToArea(mesh.areas[areaIds[0]], from)];
  const kinds: GeneratedNavLinkKind[] = ["walk"];
  for (let i = 1; i < areaIds.length; i++) {
    const prev = mesh.areas[areaIds[i - 1]];
    const next = mesh.areas[areaIds[i]];
    points.push(portalPoint(prev, next));
    kinds.push(mesh.areaLinks[`${areaIds[i - 1]}:${areaIds[i]}`]?.kind ?? "walk");
  }
  points.push(projectPointToArea(mesh.areas[areaIds[areaIds.length - 1]], to));
  kinds.push("walk");
  return compactPointsWithKinds(points, kinds);
}

function generatedAreaNeighbors(area: GeneratedNavArea): number[] {
  const out: number[] = [];
  for (const list of Object.values(area.connections)) {
    for (const id of list || []) if (!out.includes(id)) out.push(id);
  }
  return out;
}

function reconstructGeneratedAreaPath(cameFrom: Int32Array, goalId: number): number[] {
  const path: number[] = [];
  for (let id = goalId; id >= 0; id = cameFrom[id]) path.push(id);
  return path.reverse();
}

function navBounds(boxes: SolidBox[], map: MapJson, cfg: GeneratedNavConfig): GeneratedNavMesh["bounds"] {
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (const box of boxes) {
    minX = Math.min(minX, box.minX);
    maxX = Math.max(maxX, box.maxX);
    minZ = Math.min(minZ, box.minZ);
    maxZ = Math.max(maxZ, box.maxZ);
  }
  for (const spawn of map.spawns || []) {
    minX = Math.min(minX, spawn[0]);
    maxX = Math.max(maxX, spawn[0]);
    minZ = Math.min(minZ, spawn[2]);
    maxZ = Math.max(maxZ, spawn[2]);
  }
  if (!Number.isFinite(minX)) return { minX: 0, maxX: 0, minZ: 0, maxZ: 0 };
  return {
    minX: snapToGrid(minX - cfg.padding, cfg.cellSize),
    maxX: snapToGrid(maxX + cfg.padding, cfg.cellSize),
    minZ: snapToGrid(minZ - cfg.padding, cfg.cellSize),
    maxZ: snapToGrid(maxZ + cfg.padding, cfg.cellSize),
  };
}

function walkableSeeds(map: MapJson, boxes: SolidBox[], cfg: GeneratedNavConfig): Array<{ point: NavPoint; normal: NavPoint }> {
  const seeds: Array<{ point: NavPoint; normal: NavPoint }> = [];
  for (const spawn of map.spawns || []) {
    const point = { x: snapToGrid(spawn[0], cfg.cellSize), y: spawn[1] || 0, z: snapToGrid(spawn[2], cfg.cellSize) };
    const ground = findGroundForNode(point, boxes, cfg);
    if (ground) seeds.push(ground);
  }
  return seeds;
}

function connectNodes(from: GeneratedNavNode, to: GeneratedNavNode, dir: GeneratedNavDir, trace: TraceResult, cfg: GeneratedNavConfig): void {
  from.links[dir] = {
    to: to.id,
    obstacleHeight: Math.max(0, trace.obstacleHeight),
    obstacleStartDist: Math.max(0, Math.min(cfg.cellSize, trace.obstacleStartDist)),
    obstacleEndDist: Math.max(0, Math.min(cfg.cellSize, trace.obstacleEndDist)),
  };
}

function traceAdjacentNode(from: NavPoint, rawTarget: NavPoint, boxes: SolidBox[], cfg: GeneratedNavConfig): TraceResult {
  const targetGround = findGroundForNode(rawTarget, boxes, cfg);
  if (!targetGround) return failedTrace(rawTarget, "no-target-ground");
  const target = targetGround.point;
  const deltaY = target.y - from.y;
  if (deltaY > cfg.jumpHeight + cfg.stepHeight) return failedTrace(target, "target-too-high");
  if (from.y - target.y > cfg.deathDrop) return failedTrace(target, "drop-too-far");
  const obstacle = obstacleOnSegment(from, target, boxes, cfg);
  if (obstacle.height > cfg.jumpHeight + cfg.stepHeight) return failedTrace(target, `obstacle-too-high:${obstacle.height.toFixed(1)}`);
  if (!hasClearBodyLine(from, target, boxes, cfg, Math.max(cfg.stepHeight, obstacle.height))) return failedTrace(target, "body-line-blocked");
  return {
    ok: true,
    end: target,
    normal: targetGround.normal,
    obstacleHeight: deltaY > cfg.stepHeight ? Math.max(deltaY, obstacle.height) : obstacle.height,
    obstacleStartDist: obstacle.startDist,
    obstacleEndDist: obstacle.endDist,
    reason: "ok",
  };
}

function failedTrace(end: NavPoint, reason = "failed"): TraceResult {
  return { ok: false, end, normal: { x: 0, y: 1, z: 0 }, obstacleHeight: 0, obstacleStartDist: 0, obstacleEndDist: 0, reason };
}

function findGroundForNode(point: NavPoint, boxes: SolidBox[], cfg: GeneratedNavConfig): { point: NavPoint; normal: NavPoint } | null {
  let best: { y: number; normal: NavPoint } | null = null;
  for (const box of boxes) {
    if (!box.surface) continue;
    if (point.x < box.minX || point.x > box.maxX || point.z < box.minZ || point.z > box.maxZ) continue;
    const y = surfaceYAtGenerated(box, point.x, point.z);
    if (y > point.y + cfg.jumpHeight + cfg.stepHeight) continue;
    if (!best || y > best.y) best = { y, normal: box.ramp ? rampNormal(box) : { x: 0, y: 1, z: 0 } };
  }
  if (!best) return null;
  const grounded = { x: point.x, y: best.y, z: point.z };
  if (!isBodyClearAt(grounded, boxes, cfg, cfg.playerHeight)) return null;
  if (best.normal.y < cfg.slopeLimit) return null;
  return { point: grounded, normal: best.normal };
}

function hasClearBodyLine(from: NavPoint, to: NavPoint, boxes: SolidBox[], cfg: GeneratedNavConfig, lift: number): boolean {
  const samples = Math.max(1, Math.ceil(Math.hypot(to.x - from.x, to.z - from.z) / Math.max(1, cfg.cellSize / 3)));
  for (let i = 0; i <= samples; i++) {
    const t = i / samples;
    const point = {
      x: from.x + (to.x - from.x) * t,
      y: from.y + (to.y - from.y) * t + lift,
      z: from.z + (to.z - from.z) * t,
    };
    if (!isBodyClearAt(point, boxes, cfg, cfg.playerHeight - lift)) return false;
  }
  return true;
}

function isBodyClearAt(point: NavPoint, boxes: SolidBox[], cfg: GeneratedNavConfig, height: number): boolean {
  const top = point.y + Math.max(1, height);
  for (const box of boxes) {
    if (box.ramp) continue;
    if (point.x < box.minX - cfg.playerRadius || point.x > box.maxX + cfg.playerRadius) continue;
    if (point.z < box.minZ - cfg.playerRadius || point.z > box.maxZ + cfg.playerRadius) continue;
    if (box.baseY < top - cfg.floorTolerance && box.topY > point.y + cfg.floorTolerance) return false;
  }
  return true;
}

function checkNodeCrouchAndBlocked(node: GeneratedNavNode, boxes: SolidBox[], cfg: GeneratedNavConfig): void {
  for (const corner of CORNERS) {
    const offset = cornerOffset(corner, cfg.cellSize / 2);
    const point = { x: node.x + offset.x, y: node.y, z: node.z + offset.z };
    if (!isBodyClearAt(point, boxes, cfg, cfg.playerHeight)) {
      node.crouch[corner] = isBodyClearAt(point, boxes, cfg, Math.max(1, cfg.playerHeight * 0.55));
      node.blocked[corner] = !node.crouch[corner];
    }
  }
}

function obstacleOnSegment(from: NavPoint, to: NavPoint, boxes: SolidBox[], cfg: GeneratedNavConfig): { height: number; startDist: number; endDist: number } {
  let height = 0;
  let startDist = cfg.cellSize;
  let endDist = 0;
  const dx = to.x - from.x;
  const dz = to.z - from.z;
  const len = Math.max(0.0001, Math.hypot(dx, dz));
  const headTop = Math.max(from.y, to.y) + cfg.playerHeight;
  for (const box of boxes) {
    if (box.ramp) continue;
    if (box.baseY > headTop + cfg.floorTolerance) continue;
    const clipped = clipSegmentAabb2D(from, to, box.minX - cfg.playerRadius, box.minZ - cfg.playerRadius, box.maxX + cfg.playerRadius, box.maxZ + cfg.playerRadius);
    if (!clipped) continue;
    const climb = box.topY - Math.min(from.y, to.y);
    if (climb <= cfg.stepHeight) continue;
    height = Math.max(height, climb);
    startDist = Math.min(startDist, clipped.t0 * len);
    endDist = Math.max(endDist, clipped.t1 * len);
  }
  return { height, startDist: height ? startDist : 0, endDist: height ? endDist : cfg.cellSize };
}

function createNavAreasFromNodes(nodes: GeneratedNavNode[], cfg: GeneratedNavConfig): GeneratedNavArea[] {
  const areas: GeneratedNavArea[] = [];
  let remaining = nodes.length;
  for (let tryWidth = cfg.maxAreaSize, tryHeight = cfg.maxAreaSize; remaining > 0 && tryWidth > 0 && tryHeight > 0;) {
    for (const node of nodes) {
      if (node.covered) continue;
      if (!testArea(node, tryWidth, tryHeight, nodes)) continue;
      const area = buildArea(node, tryWidth, tryHeight, nodes, areas.length);
      areas.push(area);
      remaining -= tryWidth * tryHeight;
    }
    if (tryWidth >= tryHeight) tryWidth--;
    else tryHeight--;
  }
  for (const node of nodes) {
    if (node.covered) continue;
    areas.push(buildPointArea(node, areas.length, cfg));
  }
  return areas;
}

function testArea(node: GeneratedNavNode, width: number, height: number, nodes: GeneratedNavNode[]): boolean {
  if (node.blocked.se) return false;
  const attrs = nodeAttrs(node);
  const normal = node.normal;
  const d = -(normal.x * node.x + normal.y * node.y + normal.z * node.z);
  for (let y = 0; y <= height; y++) {
    let row = y === 0 ? node : walk(nodes, node, "south", y);
    if (!row) return false;
    for (let x = 0; x <= width; x++) {
      if (!row) return false;
      if (x < width && y < height) {
        if (row.covered || !isClosedCell(row, nodes)) return false;
        if (nodeAttrs(row) !== attrs) return false;
        if (Math.abs(normal.x * row.x + normal.y * row.y + normal.z * row.z + d) > 5) return false;
        if ((width > 1 || height > 1) && hasInternalObstacle(row)) return false;
      }
      row = row.links.east ? nodes[row.links.east.to] : null as any;
    }
  }
  return true;
}

function buildArea(node: GeneratedNavNode, width: number, height: number, nodes: GeneratedNavNode[], id: number): GeneratedNavArea {
  const nw = node;
  const ne = walk(nodes, node, "east", width)!;
  const sw = walk(nodes, node, "south", height)!;
  const se = walk(nodes, ne, "south", height)!;
  for (let y = 0; y < height; y++) {
    let row = y === 0 ? node : walk(nodes, node, "south", y)!;
    for (let x = 0; x < width; x++) {
      row.covered = true;
      row.areaId = id;
      row = nodes[row.links.east!.to];
    }
  }
  return {
    id,
    nw: pointOf(nw),
    ne: pointOf(ne),
    se: pointOf(se),
    sw: pointOf(sw),
    attrs: nodeAttrs(node) | (hasInternalObstacle(node) ? 4 : 0),
    connections: {},
    nodes: { nw: nw.id, ne: ne.id, se: se.id, sw: sw.id },
  };
}

function buildPointArea(node: GeneratedNavNode, id: number, cfg: GeneratedNavConfig): GeneratedNavArea {
  const half = cfg.cellSize * 0.35;
  node.covered = true;
  node.areaId = id;
  return {
    id,
    nw: { x: node.x - half, y: node.y, z: node.z - half },
    ne: { x: node.x + half, y: node.y, z: node.z - half },
    se: { x: node.x + half, y: node.y, z: node.z + half },
    sw: { x: node.x - half, y: node.y, z: node.z + half },
    attrs: nodeAttrs(node) | (hasInternalObstacle(node) ? 4 : 0),
    connections: {},
    nodes: { nw: node.id, ne: node.id, se: node.id, sw: node.id },
  };
}

function connectGeneratedAreas(nodes: GeneratedNavNode[], areas: GeneratedNavArea[]): void {
  for (const area of areas) {
    scanAreaEdge(area, "north", nodes, areas);
    scanAreaEdge(area, "east", nodes, areas);
    scanAreaEdge(area, "south", nodes, areas);
    scanAreaEdge(area, "west", nodes, areas);
  }
}

function buildAreaLinks(nodes: GeneratedNavNode[], areas: GeneratedNavArea[], cfg: GeneratedNavConfig): Record<string, GeneratedNavAreaLink> {
  const links: Record<string, GeneratedNavAreaLink> = {};
  for (const area of areas) {
    for (const dir of DIRS) {
      for (const nodeId of edgeNodeIds(area, dir, nodes)) {
        const node = nodes[nodeId];
        const link = node.links[dir];
        if (!link) continue;
        const targetAreaId = nodes[link.to]?.areaId;
        if (targetAreaId == null || targetAreaId === area.id || !areas[targetAreaId]) continue;
        const reverseKey = `${targetAreaId}:${area.id}`;
        const key = `${area.id}:${targetAreaId}`;
        const candidate = makeAreaLink(area, areas[targetAreaId], link, cfg);
        const existing = links[key];
        if (!existing || candidate.cost < existing.cost) links[key] = candidate;
        if (!links[reverseKey]) {
          links[reverseKey] = makeAreaLink(areas[targetAreaId], area, link, cfg);
        }
      }
    }
  }
  return links;
}

function makeAreaLink(from: GeneratedNavArea, to: GeneratedNavArea, nodeLink: GeneratedNavNodeLink, cfg: GeneratedNavConfig): GeneratedNavAreaLink {
  const fromCenter = areaCenter(from);
  const toCenter = areaCenter(to);
  const heightDelta = toCenter.y - fromCenter.y;
  const distance = Math.hypot(toCenter.x - fromCenter.x, heightDelta, toCenter.z - fromCenter.z);
  const obstacleHeight = Math.max(nodeLink.obstacleHeight || 0, heightDelta > cfg.stepHeight ? heightDelta : 0);
  const crouch = (from.attrs & 1) !== 0 || (to.attrs & 1) !== 0;
  const kind = crouch ? "crouch" : heightDelta > cfg.stepHeight ? "jump" : heightDelta < -cfg.stepHeight ? "drop" : "walk";
  const jumpPenalty = heightDelta > cfg.stepHeight ? cfg.jumpPenalty + Math.max(0, heightDelta - cfg.stepHeight) * 6 : 0;
  const dropPenalty = heightDelta < -cfg.stepHeight ? cfg.dropPenalty + Math.max(0, -heightDelta - cfg.stepHeight) * 3 : 0;
  const crouchPenalty = crouch ? cfg.crouchPenalty : 0;
  const obstaclePenalty = obstacleHeight > cfg.stepHeight ? (obstacleHeight - cfg.stepHeight) * 8 : 0;
  const deathDropPenalty = heightDelta < -cfg.deathDrop ? Infinity : 0;
  const jumpLimitPenalty = heightDelta > cfg.jumpHeight + cfg.stepHeight ? Infinity : 0;
  return {
    from: from.id,
    to: to.id,
    distance,
    heightDelta,
    obstacleHeight,
    cost: distance + jumpPenalty + dropPenalty + crouchPenalty + obstaclePenalty + deathDropPenalty + jumpLimitPenalty,
    kind,
  };
}

/**
 * Wire ladder regions into the area graph as bidirectional `ladder` links.
 * Mirrors lever's nav ladders (`CNavLadder`) which `NavAreaBuildPath` walks
 * separately from floor connections: a ladder connects the walkable area at its
 * foot to the walkable area at its top, regardless of the vertical gap (the
 * normal walk/jump links forbid that climb because it exceeds jump height).
 * Without this, maps with ladders leave the upper and lower layers in disjoint
 * graph components and the bot can never path between them.
 */
function connectLadderAreas(
  ladders: LadderRegion[],
  areas: GeneratedNavArea[],
  links: Record<string, GeneratedNavAreaLink>,
  cfg: GeneratedNavConfig,
): void {
  for (const ladder of ladders) {
    const cx = (ladder.minX + ladder.maxX) / 2;
    const cz = (ladder.minZ + ladder.maxZ) / 2;
    const bottom = nearestAreaToLadderEnd(areas, cx, cz, ladder.baseY, cfg);
    const top = nearestAreaToLadderEnd(areas, cx, cz, ladder.topY, cfg);
    if (bottom == null || top == null || bottom === top) continue;
    addLadderLink(areas, links, bottom, top);
    addLadderLink(areas, links, top, bottom);
  }
}

function nearestAreaToLadderEnd(
  areas: GeneratedNavArea[],
  x: number,
  z: number,
  y: number,
  cfg: GeneratedNavConfig,
): number | null {
  const maxHorizontal = cfg.cellSize * 2 + cfg.ladderReach;
  const maxVertical = cfg.stepHeight + cfg.jumpHeight;
  let best: { id: number; score: number } | null = null;
  for (const area of areas) {
    const closest = closestPointOnArea(area, { x, y, z });
    const horizontal = Math.hypot(closest.x - x, closest.z - z);
    const vertical = Math.abs(closest.y - y);
    if (horizontal > maxHorizontal || vertical > maxVertical) continue;
    const score = horizontal + vertical;
    if (!best || score < best.score) best = { id: area.id, score };
  }
  return best?.id ?? null;
}

function addLadderLink(
  areas: GeneratedNavArea[],
  links: Record<string, GeneratedNavAreaLink>,
  fromId: number,
  toId: number,
): void {
  const from = areas[fromId];
  const to = areas[toId];
  const fromCenter = areaCenter(from);
  const toCenter = areaCenter(to);
  const heightDelta = toCenter.y - fromCenter.y;
  const distance = Math.hypot(toCenter.x - fromCenter.x, heightDelta, toCenter.z - fromCenter.z);
  links[`${fromId}:${toId}`] = {
    from: fromId,
    to: toId,
    distance,
    heightDelta,
    obstacleHeight: 0,
    // Ladders are cheap and safe relative to a free climb: charge the climb
    // distance plus a small mount/dismount overhead so the planner prefers a
    // ladder over an equivalent jump chain but not over flat ground.
    cost: distance + LADDER_MOUNT_COST,
    kind: "ladder",
  };
  const dir = ladderConnectionDir(fromCenter, toCenter);
  const list = (from.connections[dir] ||= []);
  if (!list.includes(toId)) list.push(toId);
}

const LADDER_MOUNT_COST = 40;

function ladderConnectionDir(from: NavPoint, to: NavPoint): GeneratedNavDir {
  const dx = to.x - from.x;
  const dz = to.z - from.z;
  if (Math.abs(dx) >= Math.abs(dz)) return dx >= 0 ? "east" : "west";
  return dz >= 0 ? "south" : "north";
}

/**
 * Default per-traversal cost functor. Reproduces lever's `CSimpleBotPathCost`
 * (`game/server/NextBot/simple_bot.h`):
 *   - start area (no `fromArea`) costs 0
 *   - a forbidden link returns a negative number (dead end)
 *   - otherwise `cost = lengthSoFar + edgeDistance + heightPenalties + danger`
 *
 * The base graph already bakes the precise jump/drop/crouch/death-drop
 * penalties into `link.cost`, so the default functor honours those while still
 * letting per-bot ability (`maxJumpHeight`, `canUseLadders`,
 * `deathDropHeight`) and a danger layer re-forbid or re-weight a link.
 */
export function makeStaticPathCost(options: PathCostOptions = {}): GeneratedPathCost {
  const stepHeight = options.stepHeight ?? DEFAULT_NAVMESH_CONFIG.stepHeight;
  const maxJumpHeight = options.maxJumpHeight ?? DEFAULT_NAVMESH_CONFIG.jumpHeight + stepHeight;
  const deathDropHeight = options.deathDropHeight ?? 240;
  const canUseLadders = options.canUseLadders ?? true;
  const jumpPenaltyScale = options.jumpPenaltyScale ?? 1;
  return (area, fromArea, link, lengthSoFar) => {
    if (!fromArea || !link) return 0;
    if (link.kind === "ladder" && !canUseLadders) return -1;
    const heightDelta = link.heightDelta;
    // Per-bot ability gates (lever forbids with -1).
    if (heightDelta > maxJumpHeight) return -1;
    if (heightDelta < -deathDropHeight) return -1;
    let cost = lengthSoFar + link.distance;
    // The static link cost already includes jump/drop/crouch/obstacle penalties;
    // fold the remainder in beyond the raw distance so the graph's tuned values
    // are preserved while jumpPenaltyScale lets a bot weight climbing higher.
    const bakedPenalty = Math.max(0, link.cost - link.distance);
    cost += link.kind === "jump" ? bakedPenalty * jumpPenaltyScale : bakedPenalty;
    if (options.dangerCost) cost += options.dangerCost(area);
    if (!Number.isFinite(cost)) return -1;
    return cost;
  };
}


function scanAreaEdge(area: GeneratedNavArea, dir: GeneratedNavDir, nodes: GeneratedNavNode[], areas: GeneratedNavArea[]): void {
  const ids = edgeNodeIds(area, dir, nodes);
  for (const id of ids) {
    const node = nodes[id];
    const link = node.links[dir];
    if (!link) continue;
    const targetArea = nodes[link.to].areaId;
    if (targetArea == null || targetArea === area.id || !areas[targetArea]) continue;
    const list = (area.connections[dir] ||= []);
    if (!list.includes(targetArea)) list.push(targetArea);
  }
}

function edgeNodeIds(area: GeneratedNavArea, dir: GeneratedNavDir, nodes: GeneratedNavNode[]): number[] {
  const out: number[] = [];
  const start = dir === "north" || dir === "west" ? area.nodes.nw : dir === "east" ? area.nodes.ne : area.nodes.sw;
  const along: GeneratedNavDir = dir === "north" || dir === "south" ? "east" : "south";
  const end = dir === "north" ? area.nodes.ne : dir === "south" ? area.nodes.se : dir === "east" ? area.nodes.se : area.nodes.sw;
  let node = nodes[start];
  while (node) {
    out.push(node.id);
    if (node.id === end) break;
    const link = node.links[along];
    if (!link) break;
    node = nodes[link.to];
  }
  return out;
}

function isClosedCell(node: GeneratedNavNode, nodes: GeneratedNavNode[]): boolean {
  const east = node.links.east ? nodes[node.links.east.to] : null;
  const south = node.links.south ? nodes[node.links.south.to] : null;
  if (!east || !south) return false;
  const eastSouth = east.links.south ? nodes[east.links.south.to] : null;
  const southEast = south.links.east ? nodes[south.links.east.to] : null;
  return !!eastSouth && !!southEast && eastSouth.id === southEast.id && east.links.west?.to === node.id && south.links.north?.to === node.id;
}

function hasInternalObstacle(node: GeneratedNavNode): boolean {
  return DIRS.some((dir) => (node.links[dir]?.obstacleHeight || 0) > DEFAULT_NAVMESH_CONFIG.stepHeight);
}

function nodeAttrs(node: GeneratedNavNode): number {
  return CORNERS.some((corner) => node.crouch[corner]) ? 1 : 0;
}

function walk(nodes: GeneratedNavNode[], node: GeneratedNavNode, dir: GeneratedNavDir, count: number): GeneratedNavNode | null {
  let current: GeneratedNavNode | null = node;
  for (let i = 0; i < count; i++) {
    if (!current?.links[dir]) return null;
    current = nodes[current.links[dir]!.to];
  }
  return current;
}

function summarizeGeneratedNav(nodes: GeneratedNavNode[], seeds: number): GeneratedNavMesh["stats"] {
  let nodeLinks = 0;
  let oneWayLinks = 0;
  let blockedQuadrants = 0;
  let crouchQuadrants = 0;
  let obstacleLinks = 0;
  let isolatedNodes = 0;
  let uncoveredNodes = 0;
  for (const node of nodes) {
    if (node.areaId == null) uncoveredNodes++;
    for (const corner of CORNERS) {
      if (node.blocked[corner]) blockedQuadrants++;
      if (node.crouch[corner]) crouchQuadrants++;
    }
    let links = 0;
    for (const dir of DIRS) {
      const link = node.links[dir];
      if (!link) continue;
      links++;
      nodeLinks++;
      if (link.obstacleHeight > DEFAULT_NAVMESH_CONFIG.stepHeight) obstacleLinks++;
      const reverse = nodes[link.to]?.links[OPPOSITE[dir]];
      if (!reverse || reverse.to !== node.id) oneWayLinks++;
    }
    if (links === 0) isolatedNodes++;
  }
  return { seeds, nodeLinks, oneWayLinks, blockedQuadrants, crouchQuadrants, obstacleLinks, isolatedNodes, uncoveredNodes };
}

function areaBounds(area: GeneratedNavArea): { minX: number; maxX: number; minZ: number; maxZ: number } {
  return {
    minX: Math.min(area.nw.x, area.ne.x, area.se.x, area.sw.x),
    maxX: Math.max(area.nw.x, area.ne.x, area.se.x, area.sw.x),
    minZ: Math.min(area.nw.z, area.ne.z, area.se.z, area.sw.z),
    maxZ: Math.max(area.nw.z, area.ne.z, area.se.z, area.sw.z),
  };
}

function areaCenter(area: GeneratedNavArea): NavPoint {
  const x = (area.nw.x + area.ne.x + area.se.x + area.sw.x) / 4;
  const z = (area.nw.z + area.ne.z + area.se.z + area.sw.z) / 4;
  return { x, y: areaYAt(area, x, z), z };
}

function areaYAt(area: GeneratedNavArea, x: number, z: number): number {
  const bounds = areaBounds(area);
  const dx = Math.max(0.0001, bounds.maxX - bounds.minX);
  const dz = Math.max(0.0001, bounds.maxZ - bounds.minZ);
  const u = Math.max(0, Math.min(1, (x - bounds.minX) / dx));
  const v = Math.max(0, Math.min(1, (z - bounds.minZ) / dz));
  const north = area.nw.y + u * (area.ne.y - area.nw.y);
  const south = area.sw.y + u * (area.se.y - area.sw.y);
  return north + v * (south - north);
}

function closestPointOnArea(area: GeneratedNavArea, point: NavPoint): NavPoint {
  const bounds = areaBounds(area);
  const x = Math.max(bounds.minX, Math.min(bounds.maxX, point.x));
  const z = Math.max(bounds.minZ, Math.min(bounds.maxZ, point.z));
  return { x, y: areaYAt(area, x, z), z };
}

function projectPointToArea(area: GeneratedNavArea, point: NavPoint): NavPoint {
  const projected = closestPointOnArea(area, point);
  return { x: projected.x, y: projected.y, z: projected.z };
}

function portalPoint(from: GeneratedNavArea, to: GeneratedNavArea): NavPoint {
  const a = areaBounds(from);
  const b = areaBounds(to);
  const overlapX0 = Math.max(a.minX, b.minX);
  const overlapX1 = Math.min(a.maxX, b.maxX);
  const overlapZ0 = Math.max(a.minZ, b.minZ);
  const overlapZ1 = Math.min(a.maxZ, b.maxZ);
  const centerA = areaCenter(from);
  const centerB = areaCenter(to);
  let x: number;
  let z: number;
  if (overlapX0 <= overlapX1) {
    x = (overlapX0 + overlapX1) / 2;
    z = centerB.z < centerA.z ? a.minZ : a.maxZ;
  } else if (overlapZ0 <= overlapZ1) {
    x = centerB.x < centerA.x ? a.minX : a.maxX;
    z = (overlapZ0 + overlapZ1) / 2;
  } else {
    const fromPoint = closestPointOnArea(from, centerB);
    const toPoint = closestPointOnArea(to, centerA);
    x = (fromPoint.x + toPoint.x) / 2;
    z = (fromPoint.z + toPoint.z) / 2;
  }
  return { x, y: (areaYAt(from, x, z) + areaYAt(to, x, z)) / 2, z };
}

function compactPointsWithKinds(points: NavPoint[], kinds: GeneratedNavLinkKind[]): { points: NavPoint[]; kinds: GeneratedNavLinkKind[] } {
  const outPoints: NavPoint[] = [];
  const outKinds: GeneratedNavLinkKind[] = [];
  for (let i = 0; i < points.length; i++) {
    const point = points[i];
    const prev = outPoints[outPoints.length - 1];
    if (prev && Math.hypot(prev.x - point.x, prev.y - point.y, prev.z - point.z) < 1) {
      const last = outKinds.length - 1;
      outKinds[last] = moreSignificantKind(outKinds[last], kinds[i]);
      continue;
    }
    outPoints.push(point);
    outKinds.push(kinds[i]);
  }
  return { points: outPoints, kinds: outKinds };
}

const KIND_SIGNIFICANCE: Record<GeneratedNavLinkKind, number> = {
  walk: 0,
  crouch: 1,
  drop: 2,
  jump: 3,
  ladder: 4,
};

function moreSignificantKind(a: GeneratedNavLinkKind, b: GeneratedNavLinkKind): GeneratedNavLinkKind {
  return KIND_SIGNIFICANCE[b] > KIND_SIGNIFICANCE[a] ? b : a;
}

function dist2D(a: NavPoint, b: NavPoint): number {
  return Math.hypot(a.x - b.x, a.z - b.z);
}

function pointOf(node: GeneratedNavNode): NavPoint {
  return { x: node.x, y: node.y, z: node.z };
}

function cornerOffset(corner: GeneratedNavCorner, size: number): { x: number; z: number } {
  return {
    x: corner === "ne" || corner === "se" ? size : -size,
    z: corner === "sw" || corner === "se" ? size : -size,
  };
}

function withinBounds(point: NavPoint, bounds: GeneratedNavMesh["bounds"], cfg: GeneratedNavConfig): boolean {
  return point.x >= bounds.minX - cfg.cellSize && point.x <= bounds.maxX + cfg.cellSize && point.z >= bounds.minZ - cfg.cellSize && point.z <= bounds.maxZ + cfg.cellSize;
}

function nodeKey(point: NavPoint, step: number): string {
  return `${snapToGrid(point.x, step)},${Math.round(point.y * 10) / 10},${snapToGrid(point.z, step)}`;
}

function snapToGrid(value: number, step: number): number {
  return Math.round(value / step) * step;
}

function surfaceYAtGenerated(box: SolidBox, x: number, z: number): number {
  if (box.ramp && box.rampStartX != null && box.rampStartZ != null && box.rampEndX != null && box.rampEndZ != null) {
    const dx = box.rampEndX - box.rampStartX;
    const dz = box.rampEndZ - box.rampStartZ;
    const lenSq = dx * dx + dz * dz;
    const t = lenSq <= 0 ? 0 : Math.max(0, Math.min(1, ((x - box.rampStartX) * dx + (z - box.rampStartZ) * dz) / lenSq));
    return box.baseY + (box.topY - box.baseY) * t;
  }
  return box.topY;
}

function rampNormal(box: SolidBox): NavPoint {
  if (!box.ramp || box.rampStartX == null || box.rampStartZ == null || box.rampEndX == null || box.rampEndZ == null) return { x: 0, y: 1, z: 0 };
  const dx = box.rampEndX - box.rampStartX;
  const dz = box.rampEndZ - box.rampStartZ;
  const dy = box.topY - box.baseY;
  const len = Math.max(0.0001, Math.hypot(dx, dz));
  const slope = dy / len;
  const nx = -(dx / len) * slope;
  const nz = -(dz / len) * slope;
  const ny = 1;
  const normalLen = Math.hypot(nx, ny, nz);
  return { x: nx / normalLen, y: ny / normalLen, z: nz / normalLen };
}

function clipSegmentAabb2D(from: NavPoint, to: NavPoint, minX: number, minZ: number, maxX: number, maxZ: number): { t0: number; t1: number } | null {
  let t0 = 0;
  let t1 = 1;
  const axes = [
    [from.x, to.x - from.x, minX, maxX],
    [from.z, to.z - from.z, minZ, maxZ],
  ];
  for (const [origin, direction, min, max] of axes) {
    if (Math.abs(direction) < 1e-8) {
      if (origin < min || origin > max) return null;
      continue;
    }
    let near = (min - origin) / direction;
    let far = (max - origin) / direction;
    if (near > far) [near, far] = [far, near];
    t0 = Math.max(t0, near);
    t1 = Math.min(t1, far);
    if (t0 > t1) return null;
  }
  return { t0, t1 };
}
